import { useEffect, useRef, useState } from "react";
import { generatePath, Outlet, useLocation, useMatch, useParams } from "react-router";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import {
  AnalyticsEvents,
  AnalyticsSourceScope,
  type NavigationSource,
  type OpenedSearchResult,
  useTrack,
  useViewTrackedOnce,
} from "@acto/taskpane/core/analytics";
import { ShellCloseButton, ShellTopBar } from "@acto/taskpane/field-sales-2/components/patterns/ShellTopBar";
import { CyclingLoaderPill } from "@acto/taskpane/field-sales-2/components/patterns/CyclingLoaderPill";
import { ReturnOriginProvider, useIsFromGroupView, useReturnOrigin } from "@acto/taskpane/field-sales-2/navigation";
import { ProductDrillProvider } from "@acto/taskpane/field-sales-2/pages/analysis/ProductDrillContext";
import { usePageEnterAnimation } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { useOrdersInRevenueDetailEnabled } from "@acto/taskpane/field-sales-2/data-access/useOrdersInRevenueDetailEnabled";
import { useCustomerWithActivityYears } from "@acto/taskpane/field-sales-2/data-access/useCustomerWithActivityYears";
import type { Customer, Kpi } from "@acto/taskpane/core/domain";
import { useStructuredMeetingPrep } from "@acto/taskpane/field-sales-2/data-access/meeting-prep/useStructuredMeetingPrep";
import { useRemovedMeetingPrepStories } from "@acto/taskpane/core/data-access/meeting-prep-story-removals";
import { firstVisibleStoryHeadline } from "@acto/taskpane/field-sales-2/data-access/meeting-prep/firstVisibleStoryHeadline";
import { CustomerActionsPill } from "@acto/taskpane/field-sales-2/components/patterns/TopBar";
import { Pill } from "@acto/taskpane/field-sales-2/components/primitives/Pill";
import { CreateTodoFlowProvider } from "@acto/taskpane/field-sales-2/components/patterns/CreateTodoSheet";
import { PeriodComparisonScope } from "@acto/taskpane/field-sales-2/components/patterns/PeriodComparisonScope/PeriodComparisonScope";
import {
  DEFAULT_PERIOD_SELECTION,
  resolvePeriodSelection,
  type PeriodSelection,
} from "@acto/taskpane/field-sales-2/utils/periodSelection";
import { isoToUtc, toIsoDate, todayUtc } from "@acto/web-common/dates";
import { ShellContent, ShellProvider, ShellSlotHost } from "@acto/taskpane/field-sales-2/shell";
import { CustomerBottomNav } from "./CustomerBottomNav";
import { AnalysisBottomNav } from "./AnalysisBottomNav";
import { type KpiChange, type OrdersList, useCustomerKpiSelection } from "./useCustomerKpiSelection";
import { OrderDetailsBottomNav } from "./OrderDetailsBottomNav";
import { GroupReturnButton } from "./GroupReturnButton";
import { InaccessibleCustomerRedirect } from "./InaccessibleCustomerRedirect";

export interface CustomerOutletContext {
  customer: Customer | undefined;
  isPending: boolean;
  dateRange: { startDate: string; endDate: string };
  isCustomRange: boolean;
  /** The period selection behind dateRange, for consumers that need the preset token (e.g. ambient context). */
  selection: PeriodSelection;
  selectedKpi: Kpi;
  onKpiChange: KpiChange;
  ordersList: OrdersList;
  onOrdersListChange: (list: OrdersList) => void;
  /** True while the meeting-prep loader owns the shell: header pill shown, name pill hidden. */
  isPrepGenerating: boolean;
  /** Headline of the first prepared story the user has not removed; undefined until prep has loaded. */
  prepStoryHeadline: string | undefined;
}

// Minimum time the loader stays up once shown, so a fast (cache-hit) generation can't flash the
// whole header/nav in and out. The pill still appears immediately — this only floors how briefly
// it can disappear.
const MIN_LOADER_MS = 500;

const Wrapper = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: "100vh",
  // Pages scroll at window level, so min-height alone never constrains the overlay region's
  // flex: 1. While an overlay covers the (hidden) page, the shell locks to the viewport so the
  // overlay scrolls internally and its pinned caption has a scrollport to stick against.
  // max-height, not height: the wrapper is itself a flex item, and the flex algorithm overrides
  // height while max-height clamps its result.
  '&:has([data-behind-overlay="true"])': {
    maxHeight: "100vh",
    overflow: "clip",
  },
  transformOrigin: "center top",
  transition: `transform ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}`,
  'body[data-sheet-open="true"] &': {
    transform: "scale(0.96)",
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    'body[data-sheet-open="true"] &': {
      transform: "none",
    },
  },
}));

const Content = styled(ShellContent)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  // The nav clearance and the keyboard cover the same bottom band of the layout viewport, so the
  // larger of the two is enough.
  paddingBottom: "max(var(--bottom-nav-opaque-height, 0px), var(--keyboard-height, 0px))",
  overflowX: "clip",
  // Under border-box the collapsed page floors at its own padding, so the clearance has to go
  // with it or it pushes the overlay region down the shell.
  '&[data-behind-overlay="true"]': {
    paddingBottom: 0,
  },
});

const NamePill = styled(Pill)(({ theme }) => ({
  flex: "0 1 auto",
  minWidth: 0,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
}));

const NameText = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[540],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const ORIGIN_ANALYTICS_SOURCES: Record<string, NavigationSource> = {
  [ActoRoutes.HOME]: "home",
  [ActoRoutes.CUSTOMERS]: "customers_page",
  [ActoRoutes.CALENDAR]: "calendar",
  [ActoRoutes.TODOS_OVERVIEW]: "todos_overview",
};

function CustomerShell() {
  const { id } = useParams<{ id: string }>();
  const origin = useReturnOrigin();
  const location = useLocation();
  // Frozen at mount: navigating between the shell's child routes drops location.state before the customer loads and the event fires.
  const [searchResultAtMount] = useState(
    () => (location.state as { searchResult?: OpenedSearchResult } | null)?.searchResult,
  );
  const t = useTranslations("fieldSalesTwo.meetingPrep");

  const { data: customer, isPending } = useCustomerWithActivityYears(id ?? "");
  const contentRef = useRef<HTMLDivElement>(null);

  const track = useTrack();
  useViewTrackedOnce(() => {
    if (!customer) return;
    track(AnalyticsEvents.CUSTOMER_DETAILS_OPENED, {
      customerId: customer.id,
      customerName: customer.name || "",
      source: ORIGIN_ANALYTICS_SOURCES[origin] ?? "other",
      searchMatchFields: searchResultAtMount?.matchFields,
      searchResultPosition: searchResultAtMount?.position,
    });
  }, customer?.id ?? null);

  const [selection, setSelection] = useState<PeriodSelection>(DEFAULT_PERIOD_SELECTION);
  const { selectedKpi, onKpiChange, ordersList, onOrdersListChange } = useCustomerKpiSelection();
  // Keyed on the day so a long-lived session (docked taskpane) re-anchors rolling presets when the
  // day rolls over instead of freezing the window at its mount-day.
  const dayKey = toIsoDate(todayUtc());
  const dateRange = resolvePeriodSelection(selection, isoToUtc(dayKey));
  const isCustomRange = selection.kind === "custom";

  const isOverview = useMatch({ path: ActoRoutes.CUSTOMER_DETAILS, end: true });
  const isAnalysis = Boolean(useMatch(ActoRoutes.CUSTOMER_ANALYSIS));
  const isGroupView = Boolean(useMatch(ActoRoutes.CUSTOMER_GROUP));
  const isMeetingPrep = Boolean(useMatch(ActoRoutes.MEETING_PREP));
  const isOrderDetails = Boolean(useMatch(ActoRoutes.CUSTOMER_ORDER_DETAILS));
  const isOrdersViewEnabled = useOrdersInRevenueDetailEnabled();
  const isFromGroupView = useIsFromGroupView();

  const {
    status: prepStatus,
    parts: prepParts,
    error: prepError,
  } = useStructuredMeetingPrep(customer?.externalReference);
  const { data: storyRemovals } = useRemovedMeetingPrepStories(id);
  const prepStoryHeadline = firstVisibleStoryHeadline(prepParts, storyRemovals);
  usePageEnterAnimation(contentRef);

  // A generation is only possible once there is a customer to generate for. Without an
  // externalReference nothing ever streams, so the idle status must not read as "generating".
  const hasPrepTarget = isPending || Boolean(customer?.externalReference);
  // True the instant a fresh meeting-prep generation starts (no cached parts yet), on that tab only.
  const rawPrepGenerating =
    isMeetingPrep &&
    hasPrepTarget &&
    (prepStatus === "idle" || prepStatus === "streaming") &&
    prepParts.length === 0 &&
    !prepError;

  // Show the loader immediately (pill-first), then floor its visibility for MIN_LOADER_MS.
  const [showPrepLoader, setShowPrepLoader] = useState(false);
  const shownAtRef = useRef(0);
  useEffect(() => {
    if (rawPrepGenerating) {
      if (!showPrepLoader) {
        shownAtRef.current = Date.now();
        setShowPrepLoader(true);
      }
      return;
    }
    if (!showPrepLoader) return;
    // Leaving the meeting-prep tab drops the loader at once; the floor only guards against a fast
    // (cache-hit) generation flashing the header and nav in and out while still on the tab.
    const remaining = isMeetingPrep ? MIN_LOADER_MS - (Date.now() - shownAtRef.current) : 0;
    if (remaining <= 0) {
      setShowPrepLoader(false);
      return;
    }
    const timer = window.setTimeout(() => setShowPrepLoader(false), remaining);
    return () => window.clearTimeout(timer);
  }, [rawPrepGenerating, showPrepLoader, isMeetingPrep]);

  const loaderStages = [t("loader.analyzing"), t("loader.preparing")];

  const outletContext: CustomerOutletContext = {
    customer,
    isPending,
    dateRange,
    isCustomRange,
    selection,
    selectedKpi,
    onKpiChange,
    ordersList,
    onOrdersListChange,
    isPrepGenerating: showPrepLoader,
    prepStoryHeadline,
  };

  const customerOverviewPath = id ? generatePath(ActoRoutes.CUSTOMER_DETAILS, { id }) : undefined;

  const topNav = (() => {
    // The customer overview is the top of the customer stack: its X exits all the way to Home.
    if (isOverview)
      return (
        <ShellTopBar
          leadingGrow={isFromGroupView}
          leading={isFromGroupView ? <GroupReturnButton to={origin} clientId={id} /> : <ShellCloseButton to={origin} />}
          trailing={id ? <CustomerActionsPill clientId={id} customerName={customer?.name} /> : undefined}
        />
      );
    // While meeting prep generates, the loader pill takes the centered slot and the customer name
    // pill is hidden; leadingGrow off so the pill centers against the bar, not the leading slot.
    // No leadText so it opens straight into the cycling stages rather than a static name frame.
    if (showPrepLoader)
      return (
        <ShellTopBar
          leading={<ShellCloseButton to={customerOverviewPath} />}
          center={<CyclingLoaderPill stages={loaderStages} active />}
          trailing={<ShellSlotHost slot="topBarTrailing" hiddenDuringOverlay />}
        />
      );
    // First-layer subpages keep the X but close back to the customer overview, not Home. Pages that
    // drill a level deeper (order/offer details, product groups) override this with a back arrow.
    return (
      <ShellTopBar
        leadingGrow
        leading={
          <ShellSlotHost slot="topBarLeading">
            <ShellCloseButton to={customerOverviewPath} />
            {customer?.name && (
              <NamePill variant="action">
                <NameText>{customer.name}</NameText>
              </NamePill>
            )}
          </ShellSlotHost>
        }
        trailing={<ShellSlotHost slot="topBarTrailing" hiddenDuringOverlay />}
      />
    );
  })();

  return (
    <AnalyticsSourceScope source="customer_details">
      <Wrapper>
        <ShellProvider>
          <ProductDrillProvider customerId={id}>
            <CreateTodoFlowProvider clientId={id}>
              {topNav}
              <Content ref={contentRef}>
                <PeriodComparisonScope selection={selection}>
                  <Outlet context={outletContext} />
                </PeriodComparisonScope>
              </Content>
              {isAnalysis || isGroupView ? (
                <AnalysisBottomNav
                  selectedKpi={selectedKpi}
                  onKpiChange={onKpiChange}
                  period={{ selection, onSelectionChange: setSelection }}
                />
              ) : isOrderDetails && isOrdersViewEnabled ? (
                <OrderDetailsBottomNav selectedKpi={selectedKpi} onKpiChange={onKpiChange} />
              ) : (
                <CustomerBottomNav />
              )}
            </CreateTodoFlowProvider>
          </ProductDrillProvider>
        </ShellProvider>
      </Wrapper>
    </AnalyticsSourceScope>
  );
}

export function CustomerDetailsLayout() {
  const { id } = useParams<{ id: string }>();

  // Keyed by id so the captured origin survives in-customer drills but resets on a customer switch.
  return (
    <ReturnOriginProvider key={id}>
      <InaccessibleCustomerRedirect customerId={id ?? ""}>
        <CustomerShell />
      </InaccessibleCustomerRedirect>
    </ReturnOriginProvider>
  );
}
